import {
  AssignClientApprovalRules,
  AssignClientDraftRules,
  GeneralInformationApprovalRules,
  GeneralInformationDraftRules,
  getAddressesDraftRules,
  getAddressesRules,
  getBillingModelApprovalRules,
  getBillingModelDraftRules,
  CertificationApprovalRules,
  CertificationDraftRules,
  getReviewApprovalRules,
  getReviewDraftRules,
  BadgeTemplateDraftRules,
  BadgeTemplateApprovalRules,
  ConsentFormDraftRules,
  ConsentFormApprovalRules,
  TurnstileScheduleProjectWizardRules,
  WorkerRegistrationDraftRules,
  WorkerRegistrationApprovalRules,
  validateCertificationGroupsList,
  validateFcaLearningGroupsList,
  validateTrainingGroupsList,
  getPricingAgreementDraftRules,
  getPricingAgreementApprovalRules,
  // TurnstileScheduleDraftRules,
  // TurnstileScheduleApprovalRules,
} from 'constants/form/projectNewRules';
import { IAddress, ILocation, getFallbackAddress, addressFieldRules } from './address';
import { IStepMap, INamedEntity } from './general';
import { ICertificationGroup } from './certification';
import { ITrainingGroup } from './training';
import { BadgePrintForType, Status } from './resource';
import { PaymentModel, UserModel, TurnstileScheduleModel, WorkerProfileModel } from './index';
import { IConsentForm, IConsentFormFieldConfig, IConsentFormLegal, ITimeZone } from './consentForm';
import { IBadgeTemplate, getFallbackBadgeTemplate, getFallbackVisitorBadgeTemplate, BadgeStatus, BadgeType } from './badge';
import { IS3FileResponse } from './file';
import { Role } from './user';
import { IFormRules, formatNumberWithCommas, isObject, ruleMap } from 'utils';
import { BadgeTemplateType } from 'modules/models/badge';
import { IFcaLearningGroup } from './fcaLearning';

export enum ProjectFields {
  NAME = 'name',
  DESCRIPTION = 'description',
  CATEGORY_ID = 'categoryId',
  COMMERCIAL_CONSTRUCTION_VALUE = 'ccv',
  FC_REGION = 'regionId',
  FC_NAE = 'naeId',
  TIME_ZONE = 'timeZoneId',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  PLANNED_MONTHS = 'plannedMonths',
  BILLING_MODEL_TYPE = 'billingModelType',
  BADGES_MODEL = 'badgeBillingModel',
  SEATS_MODEL = 'seatBillingModel',
  BADGING_MATCHES_JOB_SITE = 'badgingSiteAddressMatchesJobSiteAddress',
  BADGING_ADDRESS = 'badgingSiteAddress',
  BADGING_ADDRESSES = 'badgingSiteAddresses',
  JOB_SITE_ADDRESS = 'jobSiteAddress',
  JOB_SITE_LOCATIONS = 'locations',
  MAILING_ADDRESS_MATCHING_TYPE = 'mailingAddressMatchingType',
  MAILING_ADDRESS_NO_IN_BADGING_SITE_ADDRESSES = 'mailingAddressNoInBadgingSiteAddresses',
  MAILING_ADDRESS = 'mailingAddress',
  CERTIFICATION_GROUPS = 'certificationGroups',
  TRAINING_GROUPS = 'trainingGroups',
  FCA_LEARNING_GROUPS = 'fcaLearningGroups',
  RELATED_COMPANIES = 'relatedCompanies',
  CONSENT_FORM_FIELDS = 'consentFormFields',
  CONSENT_FORM_LEGALS = 'consentFormLegals',
  MUST_COMPLY_WITH_NYC_LL_196 = 'mustComplyWithNycLL196',
  PERMIT_HOLDER = 'permitHolder',
  PERMIT_NUMBER = 'permitNumber',
  LICENCE_NUMBER = 'licenseNumber',
  PRICING_AGREEMENT = 'pricingAgreement',
  BADGE_TEMPLATES = 'badgeTemplates',
  TURNSTILE_SCHEDULES = 'schedules',
  IS_SMS_ENABLED = 'isSmsEnabled',
  PROJECT_MANAGED_BY_USERS = 'managedByUsers',
}

export enum BadgesModelFields {
  BADGE_PRICE = 'badgePrice',
  BADGE_REPRINTING_COST = 'reprintingCost',
  CLIENT_PAYS = 'isBilledPerCompany',
  BILLED_COMPANY = 'billedCompany',
  BILLED_COMPANY_ID = 'billedCompanyId',
  VISITOR_BADGE_PRICE = 'visitorBadgePrice',
  VISITOR_REPRINTING_COST = 'visitorReprintingCost',
  GC_BADGE_PRICE = 'gcBadgePrintPrice',
  GC_REPRINT_PRICE = 'gcBadgeReprintPrice',
  BILLING_MODEL_RESPONSIBLE_COMPANIES = 'billingModelResponsibleCompanies',
  IS_CHANGE_IN_BILLING_MODEL = 'isChangeInBadgeBillingModel',
}

export enum SeatsModelFields {
  WORKERS_NUMBER = 'estimatedWorkersNumber',
  IS_FIXED_SEAT_PRICE = 'isFixedSeatPrice',
  SEAT_PRICE = 'seatPrice',
  BILLING_TIER = 'billingTier',
  BILLING_TIER_ID = 'billingTierId',
  BILLED_COMPANY = 'billedCompany',
  BILLED_COMPANY_ID = 'billedCompanyId',
  REPRINTING_COST = 'reprintingCost',
  VISITOR_BADGE_PRICE = 'visitorBadgePrice',
  VISITOR_REPRINTING_COST = 'visitorReprintingCost',
}

export enum CompanyRole {
  DEVELOPER = 0,
  GENERAL_CONTRACTOR = 1,
  SUB_CONTRACTOR = 2,
  DEVELOPER_PROJECT_OWNER = 3,
  UNVERIFIED_CLIENTS = 4,
}

export enum CompanyStatus {
  DRAFT = 0,
  PENDING_APPROVAL = 1,
  ACTIVE = 2,
  ARCHIVED = 3,
}

export enum MailingAddressMatchingType {
  NONE = 0,
  JOB_SITE_ADDRESS = 1,
  BADGING_SITE_ADDRESS = 2,
}

export enum SourceType {
  SYSTEM = 1,
  MIGRATION = 2,
}

export enum BillingModelType {
  BADGES = 0,
  SEATS = 1,
}

export enum CompanyProjectStatus {
  PENDING = 0,
  ACCEPTED = 1,
  REJECTED = 2,
  INVITATION_SENT = 3, // For 1 step invitation
  PENDING_WELCOME = 4, // For pending welcome kit
}

export enum WorkerProjectStatus {
  PENDING = 0,
  ACCEPTED = 1,
  DECLINED = 2,
}

export interface IAcsIdByLocation {
  location?: ILocation;
  accessControlSystems: string[];
  enableLocationManager?: boolean;
}

export interface IBillingTier {
  id: string;
  top: number;
  bottom: number;
  price: number;
}

export interface IProjectAssignUser {
  id: string;
  roleId?: string;
  groupId: string;
}

export interface IProjectCompany {
  id: string;
  role?: CompanyRole;
  name: string;
  status?: CompanyStatus;
  tempId?: string;
  isTaxExempt?: boolean;
  canViewAllWorkers?: boolean;
  canAssignAllWorkers?: boolean;
  canCreateOrManageAllWorkers?: boolean;
  isPaymentMethodAdded?: boolean;
  isVerified?: boolean;
  sponsor?: any;
}

export interface IVisitorModel {
  [BadgesModelFields.VISITOR_BADGE_PRICE]?: number;
  [BadgesModelFields.VISITOR_REPRINTING_COST]?: number;
}

export interface IGCModel {
  [BadgesModelFields.GC_BADGE_PRICE]?: number;
  [BadgesModelFields.GC_REPRINT_PRICE]?: number;
}

export interface IBadgesModel extends IVisitorModel, IGCModel {
  [BadgesModelFields.BADGE_PRICE]: number;
  [BadgesModelFields.CLIENT_PAYS]: boolean;
  [BadgesModelFields.BADGE_REPRINTING_COST]: number;
  [BadgesModelFields.BILLED_COMPANY]?: IProjectCompany;
  [BadgesModelFields.BILLED_COMPANY_ID]?: string;
  [BadgesModelFields.BILLING_MODEL_RESPONSIBLE_COMPANIES]?: Array<IBillingModelResponsibleCompany>;
  [BadgesModelFields.IS_CHANGE_IN_BILLING_MODEL]?: boolean;
}

export interface IBillingModelResponsibleCompany {
  id: string;
  billingModelId: string;
  projectId: string;
  badgePrintForType: BadgePrintForType;
  isBilledPerCompany: boolean;
  isClientsCoveredOtherClients: boolean;
  billedCompanyId: string;
  coveredCompanyId: string;
  canShowPriceToCoveredClients: boolean;
  canShowOnPricingAgreement: boolean;
}

export interface ISeatsModel extends IVisitorModel {
  [SeatsModelFields.WORKERS_NUMBER]: number;
  [SeatsModelFields.IS_FIXED_SEAT_PRICE]: boolean;
  [SeatsModelFields.SEAT_PRICE]: number;
  [SeatsModelFields.BILLING_TIER]?: IBillingTier;
  [SeatsModelFields.BILLING_TIER_ID]?: string;
  [SeatsModelFields.BILLED_COMPANY]?: IProjectCompany;
  [SeatsModelFields.BILLED_COMPANY_ID]?: string;
  [SeatsModelFields.REPRINTING_COST]: number;
}

export interface IWorkerSettings {
  emailPhoneVerificationType: number;
  allowReducedPasswordComplexity?: boolean;
  employerMustValidateWorkers?: boolean;
  createWorkerRequiresDateOfBirth?: boolean;
  workerCustomProfileFields?: WorkerProfileModel.IWorkerProfileField[];
}

export interface IProject {
  id: string;
  [ProjectFields.NAME]: string;
  [ProjectFields.DESCRIPTION]: string;
  [ProjectFields.CATEGORY_ID]: string;
  [ProjectFields.COMMERCIAL_CONSTRUCTION_VALUE]: number;
  [ProjectFields.FC_REGION]: string;
  [ProjectFields.FC_NAE]: string;
  [ProjectFields.START_DATE]: string;
  [ProjectFields.END_DATE]: string;
  [ProjectFields.PLANNED_MONTHS]: string;
  [ProjectFields.JOB_SITE_ADDRESS]: IAddress;
  [ProjectFields.JOB_SITE_LOCATIONS]: ILocation[];
  [ProjectFields.BADGING_MATCHES_JOB_SITE]: boolean;
  [ProjectFields.BADGING_ADDRESS]: IAddress;
  [ProjectFields.BADGING_ADDRESSES]: IAddress[];
  [ProjectFields.MAILING_ADDRESS_MATCHING_TYPE]: MailingAddressMatchingType;
  [ProjectFields.MAILING_ADDRESS_NO_IN_BADGING_SITE_ADDRESSES]: string | number;
  [ProjectFields.MAILING_ADDRESS]: IAddress;
  [ProjectFields.BILLING_MODEL_TYPE]: number;
  [ProjectFields.BADGES_MODEL]: IBadgesModel;
  [ProjectFields.SEATS_MODEL]: ISeatsModel;
  [ProjectFields.CERTIFICATION_GROUPS]: ICertificationGroup[];
  [ProjectFields.TRAINING_GROUPS]: ITrainingGroup[];
  [ProjectFields.FCA_LEARNING_GROUPS]?: IFcaLearningGroup[];
  [ProjectFields.CONSENT_FORM_FIELDS]: IConsentFormFieldConfig[];
  [ProjectFields.CONSENT_FORM_LEGALS]: IConsentFormLegal[];
  [ProjectFields.TIME_ZONE]?: string;
  [ProjectFields.MUST_COMPLY_WITH_NYC_LL_196]?: number;
  [ProjectFields.PERMIT_HOLDER]?: string;
  [ProjectFields.PERMIT_NUMBER]?: string;
  [ProjectFields.LICENCE_NUMBER]?: string;
  [ProjectFields.PRICING_AGREEMENT]?: string;
  setupNotes?: string;
  status: Status;
  companyProjectStatus?: CompanyProjectStatus;
  [ProjectFields.RELATED_COMPANIES]?: IProjectCompany[];
  category?: INamedEntity;
  region?: INamedEntity;
  nae?: INamedEntity;
  clientCount?: number;
  assignedCompaniesCount?: number;
  acsIdListByLocation?: IAcsIdByLocation[];
  bpsIdList?: string[];
  generalContractorBadgeTemplate?: IBadgeTemplate;
  subcontractorBadgeTemplate?: IBadgeTemplate;
  visitorBadgeTemplate?: IBadgeTemplate;
  subcontractorBadgeTemplateMatchesGeneralContractor?: boolean;
  companyHasCreditCard?: boolean;
  timeZone?: INamedEntity;
  paymentMethod?: PaymentModel.IPaymentMethod;
  generalContractors?: INamedEntity[];
  [ProjectFields.BADGE_TEMPLATES]: any[];
  clearLocationManagerAfterHrs?: number | string;
  defaultLocationManagerFlushHours?: boolean;
  deletedFcaLearningGroups?: IFcaLearningGroup[];
  schedules?: TurnstileScheduleModel.ITurnstileSchedule[];
  acsScheduleState?: TurnstileScheduleModel.ProjectAcsScheduleStates;
  workerSettings?: IWorkerSettings;
  workerGroups?: { id: string; name: string }[];
  mustComplyWithOSHA?: boolean;
  isCertificateGroupEditable?: boolean;
  presetCertificationGroups?: ICertificationGroup[];
  typeId?: string;
  subTypeId?: string;
  type?: INamedEntity;
  subType?: INamedEntity;
  companyProjectRole?: CompanyRole;
  overrideSetupForAllCompanies?: boolean;
  WorkerCustomProfileFields?: any;
  isTaxExempt?: boolean;
  skipSubcontractorsPaymentMethods?: boolean;
  skipPaymentMethodSubcontractors?: Array<INamedEntityResponse>;
  skipPaymentMethodForAllSubcontractors?: boolean;
  projectCodes?: {
    id?: string;
    code: string;
    canShowToGC: boolean;
    canShowToSub: boolean;
    projectCodeVisibilityRules?: {
      id?: string;
      companyId?: string;
      companyName?: string;
      label?: string;
      value?: string;
    }[];
  }[];
  allowUnverifiedClients?: boolean;
  unverifiedClientCreationAllowedCompanies?: any;
  sponsor?: INamedEntity;
  isSmsEnabled?: boolean;
  fcaProjectDirectorId?: string;
  fcaProjectDirector?: INamedEntity;
  managedByIds?: string[];
  managedByType?: number;
  managedByUsers?: any[]
}

export interface INamedEntityResponse {
  id: string;
  name: string;
}

export enum ProjectBadgeLogos {
  GENERAL_CONTRACTOR_BADGE_LOGO = 'generalContractorBadgeLogo',
  SUBCONTRACTOR_BADGE_LOGO = 'subcontractorBadgeLogo',
  VISITOR_BADGE_LOGO = 'visitorBadgeLogo',
}

export enum ProjectBadgeTemplates {
  GENERAL_CONTRACTOR_BADGE_TEMPLATE_FILE = 'generalContractorBadgeTemplate',
  SUBCONTRACTOR_BADGE_TEMPLATE_FILE = 'subcontractorBadgeTemplate',
  VISITOR_BADGE_TEMPLATE_FILE = 'visitorBadgeTemplate',
  TEMPLATE_FILE_NAME = 'templateFileName',
}

export interface IProjectBadgeFilesResource {
  generalContractorBadgeTemplateLogo?: IS3FileResponse;
  subcontractorBadgeTemplateLogo?: IS3FileResponse;
  visitorBadgeTemplateLogo?: IS3FileResponse;
}

export const projectBadgeResponseMap = {
  generalContractorBadgeTemplateLogo: ProjectBadgeLogos.GENERAL_CONTRACTOR_BADGE_LOGO,
  subcontractorBadgeTemplateLogo: ProjectBadgeLogos.SUBCONTRACTOR_BADGE_LOGO,
  visitorBadgeTemplateLogo: ProjectBadgeLogos.VISITOR_BADGE_LOGO,
};

export const projectBadgeKeys = [
  ProjectBadgeLogos.GENERAL_CONTRACTOR_BADGE_LOGO,
  ProjectBadgeLogos.SUBCONTRACTOR_BADGE_LOGO,
  ProjectBadgeLogos.VISITOR_BADGE_LOGO,
  ProjectBadgeTemplates.GENERAL_CONTRACTOR_BADGE_TEMPLATE_FILE,
  ProjectBadgeTemplates.SUBCONTRACTOR_BADGE_TEMPLATE_FILE,
  ProjectBadgeTemplates.VISITOR_BADGE_TEMPLATE_FILE,
  ProjectBadgeTemplates.TEMPLATE_FILE_NAME,
];

export enum ProjectStep {
  GENERAL_INFORMATION = 'general-information',
  ASSIGN_CLIENTS = 'assign-client',
  BILLING_MODEL = 'billing-model',
  BILLING_ENGINE = 'billing-engine',
  PRICING_AGREEMENT = 'pricing-agreement',
  ADDRESSES = 'addresses',
  CERTIFICATIONS_TRAININGS = 'certifications-and-trainings',
  BADGE_TEMPLATES = 'badge-templates',
  TURNSTILE_SCHEDULE = 'turnstile-schedule',
  MASS_NOTIFICATIONS = 'mass-notifications',
  WORKER_REGISTRATION = 'worker-registration',
  WORKER_CONSENT_FORM = 'worker-consent-form',
  REVIEW = 'review',
  CREDIT_CARD = 'credit-card',
  ARCHIVE = 'archive',
}

export const projectStepMap: IStepMap = {
  [ProjectStep.GENERAL_INFORMATION]: {
    key: ProjectStep.GENERAL_INFORMATION,
    title: 'General Information',
    subtitle: 'Create New Project',
    hideControls: false,
    order: 0,
    fields: [
      { name: ProjectFields.NAME, required: true },
      { name: ProjectFields.DESCRIPTION, required: false },
      { name: ProjectFields.CATEGORY_ID, required: true },
      { name: ProjectFields.COMMERCIAL_CONSTRUCTION_VALUE, required: false },
      { name: ProjectFields.FC_REGION, required: true },
      { name: ProjectFields.FC_NAE, required: true },
      { name: ProjectFields.START_DATE, required: true },
      { name: ProjectFields.END_DATE, required: true },
      { name: ProjectFields.TIME_ZONE, required: true },
      { name: ProjectFields.MUST_COMPLY_WITH_NYC_LL_196, required: false },
      { name: ProjectFields.PERMIT_HOLDER, required: false },
      { name: ProjectFields.PERMIT_NUMBER, required: false },
      { name: ProjectFields.LICENCE_NUMBER, required: false },
      {name: ProjectFields.PROJECT_MANAGED_BY_USERS, required: true }
    ],
    draftFormRules: GeneralInformationDraftRules,
    approvalFormRules: GeneralInformationApprovalRules,
  },
  [ProjectStep.ASSIGN_CLIENTS]: {
    key: ProjectStep.ASSIGN_CLIENTS,
    title: 'Companies',
    subtitle: '',
    hideControls: false,
    order: 1,
    fields: [
      {
        name: ProjectFields.RELATED_COMPANIES,
        required: true,
        fields: [{ name: 'id', required: true }],
        requiredFunc: ({ accumulator, value, field }) => {
          const hasDeveloperOrProjectOwner = !!value?.find(
            company => company.role === CompanyRole.DEVELOPER_PROJECT_OWNER || company.role === CompanyRole.DEVELOPER
          );
          const hasGeneralContractor = !!value?.find(company => company.role === CompanyRole.GENERAL_CONTRACTOR);
          const multipleBy = value?.length > 1 ? value.length : 1;
          const additionalRequired = !hasDeveloperOrProjectOwner || !hasGeneralContractor ? 1 : 0;
          return accumulator + field.fields.filter(ff => ff.required).length * multipleBy + additionalRequired;
        },
        completedFunc: ({ value = [] }) => {
          return value.filter(company => company.status === CompanyStatus.ACTIVE).length;
        },
      },
    ],
    draftFormRules: AssignClientDraftRules,
    approvalFormRules: AssignClientApprovalRules,
  },
  [ProjectStep.BILLING_MODEL]: {
    key: ProjectStep.BILLING_MODEL,
    title: 'Billing Model',
    subtitle: 'Billing Model',
    hideControls: false,
    order: 2,
    fields: [
      // { name: ProjectFields.BILLING_MODEL_TYPE, required: false, computePositive: true },
      {
        name: ProjectFields.BADGES_MODEL,
        required: true,
        fields: [
          { name: BadgesModelFields.BADGE_PRICE, required: true },
          { name: BadgesModelFields.BADGE_REPRINTING_COST, required: true },
          { name: BadgesModelFields.GC_BADGE_PRICE, required: true },
          { name: BadgesModelFields.GC_REPRINT_PRICE, required: true },
          { name: BadgesModelFields.VISITOR_BADGE_PRICE, required: true },
          { name: BadgesModelFields.VISITOR_REPRINTING_COST, required: true },
          // { name: BadgesModelFields.BILLING_MODEL_RESPONSIBLE_COMPANIES, required: true },
          // { name: BadgesModelFields.CLIENT_PAYS, required: false, computePositive: true },
          // { name: BadgesModelFields.BILLED_COMPANY_ID, required: false },
        ],
      },
      // {
      //   name: ProjectFields.SEATS_MODEL,
      //   required: false,
      //   fields: [
      //     { name: SeatsModelFields.WORKERS_NUMBER, required: false },
      //     { name: SeatsModelFields.IS_FIXED_SEAT_PRICE, required: false },
      //     { name: SeatsModelFields.SEAT_PRICE, required: false },
      //     { name: SeatsModelFields.BILLING_TIER, required: false },
      //     { name: SeatsModelFields.BILLED_COMPANY, required: false },
      //     { name: SeatsModelFields.REPRINTING_COST, required: false },
      //   ],
      // },
      { name: ProjectFields.PRICING_AGREEMENT, required: false },
    ],
    getDraftFormRules: getBillingModelDraftRules,
    getApprovalFormRules: getBillingModelApprovalRules,
  },
  [ProjectStep.BILLING_ENGINE]: {
    key: ProjectStep.BILLING_ENGINE,
    title: 'Billing Engine',
    subtitle: 'Billing Engine',
    hideControls: false,
    order: 3,
    fields: [{ name: ProjectFields.BILLING_MODEL_TYPE, required: true, computePositive: true }],
    getDraftFormRules: getBillingModelDraftRules,
    getApprovalFormRules: getBillingModelApprovalRules,
  },
  [ProjectStep.PRICING_AGREEMENT]: {
    key: ProjectStep.PRICING_AGREEMENT,
    title: 'Pricing Agreement',
    subtitle: 'Pricing Agreement',
    hideControls: false,
    order: 4,
    fields: [{ name: ProjectFields.PRICING_AGREEMENT, required: true, computePositive: true }],
    getDraftFormRules: getPricingAgreementDraftRules,
    getApprovalFormRules: getPricingAgreementApprovalRules,
  },
  [ProjectStep.ADDRESSES]: {
    key: ProjectStep.ADDRESSES,
    title: 'Addresses and Locations',
    subtitle: '',
    hideControls: false,
    order: 5,
    fields: [
      { name: ProjectFields.BADGING_MATCHES_JOB_SITE, required: false },
      { name: ProjectFields.MAILING_ADDRESS_MATCHING_TYPE, required: false },
      {
        name: ProjectFields.JOB_SITE_ADDRESS,
        required: true,
        fields: addressFieldRules.required,
      },
      {
        name: ProjectFields.JOB_SITE_LOCATIONS,
        required: true,
        fields: [{ name: 'name', required: true }],
      },
      {
        name: ProjectFields.BADGING_ADDRESSES,
        required: false,
        fields: addressFieldRules.notRequired,
      },
      {
        name: ProjectFields.BADGING_ADDRESS,
        required: false,
        fields: addressFieldRules.notRequired,
      },
      {
        name: ProjectFields.MAILING_ADDRESS,
        required: true,
        fields: addressFieldRules.notRequired,
      },
    ],
    getDraftFormRules: getAddressesDraftRules,
    getApprovalFormRules: getAddressesRules,
  },
  [ProjectStep.CERTIFICATIONS_TRAININGS]: {
    key: ProjectStep.CERTIFICATIONS_TRAININGS,
    title: 'Certifications, Trainings, and Courses',
    subtitle: '',
    hideControls: false,
    order: 6,
    fields: [
      {
        name: ProjectFields.CERTIFICATION_GROUPS,
        required: false,
        requiredFunc: ({ accumulator, value }) => {
          const errorList = validateCertificationGroupsList({ value });
          const count = isObject(errorList) ? Object.keys(errorList)?.length : 0;
          return accumulator + (count > 0 ? count : 0);
        },
        completedFunc: ({ accumulator, value }) => {
          const errorList = validateCertificationGroupsList({ value });
          const count = isObject(errorList) ? Object.keys(errorList)?.length : 0;
          return accumulator + (count > 0 ? 0 : count);
        },
      },
      {
        name: ProjectFields.TRAINING_GROUPS,
        required: false,
        requiredFunc: ({ accumulator, value }) => {
          const errorList = validateTrainingGroupsList({ value });
          const count = isObject(errorList) ? Object.keys(errorList)?.length : 0;
          return accumulator + (count > 0 ? count : 0);
        },
        completedFunc: ({ accumulator, value }) => {
          const errorList = validateTrainingGroupsList({ value });
          const count = isObject(errorList) ? Object.keys(errorList)?.length : 0;
          return accumulator + (count > 0 ? 0 : count);
        },
      },
      {
        name: ProjectFields.FCA_LEARNING_GROUPS,
        required: false,
        requiredFunc: ({ accumulator, value }) => {
          const errorList = validateFcaLearningGroupsList({ value });
          const count = isObject(errorList) ? Object.keys(errorList)?.length : 0;
          return accumulator + (count > 0 ? count : 0);
        },
        completedFunc: ({ accumulator, value }) => {
          const errorList = validateFcaLearningGroupsList({ value });
          const count = isObject(errorList) ? Object.keys(errorList)?.length : 0;
          return accumulator + (count > 0 ? 0 : count);
        },
      },
    ],
    draftFormRules: CertificationDraftRules,
    approvalFormRules: CertificationApprovalRules,
  },
  [ProjectStep.BADGE_TEMPLATES]: {
    key: ProjectStep.BADGE_TEMPLATES,
    title: 'Badge Templates',
    subtitle: '',
    order: 7,
    hideControls: false,
    fields: [
      {
        name: ProjectFields.BADGE_TEMPLATES,
        required: true,
        requiredFunc: ({ accumulator, value, field }) => {
          return accumulator + 2;
        },
        completedFunc: ({ value = [] }) => {
          let hasGeneralContractor = false;
          let hasSubcontractor = false;
          let count = 0;
          value?.forEach(({ type, status }) => {
            if (status === 0) {
              if (type === BadgeTemplateType.GeneralContractor) {
                hasGeneralContractor = true;
              }
              if (type === BadgeTemplateType.SubContractor) {
                hasSubcontractor = true;
              }
            }
          });
          if (hasGeneralContractor) count++;
          if (hasSubcontractor) count++;
          return count;
        },
      },
    ],
    draftFormRules: BadgeTemplateDraftRules,
    approvalFormRules: BadgeTemplateApprovalRules,
  },
  [ProjectStep.TURNSTILE_SCHEDULE]: {
    key: ProjectStep.TURNSTILE_SCHEDULE,
    title: 'Turnstile Schedule',
    subtitle: 'Turnstile Schedule',
    hideControls: false,
    order: 8,
    fields: [{ name: ProjectFields.TURNSTILE_SCHEDULES, required: false }],
    draftFormRules: TurnstileScheduleProjectWizardRules, // TurnstileScheduleDraftRules,
    approvalFormRules: TurnstileScheduleProjectWizardRules, // TurnstileScheduleApprovalRules,
  },
  [ProjectStep.MASS_NOTIFICATIONS]: {
    key: ProjectStep.MASS_NOTIFICATIONS,
    title: 'Mass Notifications',
    subtitle: 'Mass Notifications',
    hideControls: false,
    order: 9,
    fields: [{ name: ProjectFields.IS_SMS_ENABLED, required: false }],
    draftFormRules: {},
    approvalFormRules: {},
  },
  [ProjectStep.WORKER_REGISTRATION]: {
    key: ProjectStep.WORKER_REGISTRATION,
    title: 'Worker Registration',
    subtitle: '',
    order: 10,
    hideControls: false,
    fields: [
      {
        name: 'workerSettings',
        required: true,
        fields: [
          { name: 'emailPhoneVerificationType', required: false },
          { name: 'allowReducedPasswordComplexity', required: false },
          { name: 'employerMustValidateWorkers', required: false },
          { name: 'createWorkerRequiresDateOfBirth', required: false },
        ],
      },
    ],
    draftFormRules: WorkerRegistrationDraftRules,
    approvalFormRules: WorkerRegistrationApprovalRules,
  },
  [ProjectStep.WORKER_CONSENT_FORM]: {
    key: ProjectStep.WORKER_CONSENT_FORM,
    title: 'Worker Consent Form',
    subtitle: '',
    order: 11,
    hideControls: false,
    fields: [
      { name: ProjectFields.CONSENT_FORM_FIELDS, required: false },
      {
        name: ProjectFields.CONSENT_FORM_LEGALS,
        required: true,
        fields: [
          { name: 'body', required: true },
          { name: 'name', required: true },
        ],
      },
    ],
    draftFormRules: ConsentFormDraftRules,
    approvalFormRules: ConsentFormApprovalRules,
  },
  [ProjectStep.REVIEW]: {
    key: ProjectStep.REVIEW,
    title: 'Review',
    subtitle: '',
    order: 12,
    hideControls: true,
    fields: [],
    getDraftFormRules: getReviewDraftRules,
    getApprovalFormRules: getReviewApprovalRules,
  },
};

export const getFallbackBadgeBillingModel = () => ({
  badgePrice: null,
  isBilledPerCompany: true,
  reprintingCost: null,
  billedCompany: null,
  billedCompanyId: null,
  visitorBadgePrice: null,
  visitorReprintingCost: null,
  gcBadgePrintPrice: null,
  gcBadgeReprintPrice: null,
});

export const getFallbackSeatBillingModel = () => ({
  estimatedWorkersNumber: null,
  isFixedSeatPrice: false,
  seatPrice: null,
  billingTier: null,
  billingTierId: null,
  billedCompany: null,
  billedCompanyId: null,
  reprintingCost: null,
  visitorBadgePrice: null,
  visitorReprintingCost: null,
});

export const getFallbackProject = (): IProject => ({
  id: undefined,
  name: null,
  description: null,
  categoryId: '',
  ccv: null,
  regionId: '',
  naeId: '',
  startDate: null,
  endDate: null,
  plannedMonths: null,
  pricingAgreement: '',
  jobSiteAddress: getFallbackAddress(),
  locations: [],
  badgingSiteAddressMatchesJobSiteAddress: false,
  badgingSiteAddress: null,
  badgingSiteAddresses: [getFallbackAddress()],
  mailingAddressMatchingType: MailingAddressMatchingType.JOB_SITE_ADDRESS,
  mailingAddressNoInBadgingSiteAddresses: '',
  mailingAddress: null,
  relatedCompanies: [],
  billingModelType: BillingModelType.BADGES,
  badgeBillingModel: getFallbackBadgeBillingModel(),
  seatBillingModel: getFallbackSeatBillingModel(),
  certificationGroups: [],
  trainingGroups: [],
  status: Status.DRAFT,
  generalContractorBadgeTemplate: getFallbackBadgeTemplate(),
  subcontractorBadgeTemplate: getFallbackBadgeTemplate(),
  visitorBadgeTemplate: getFallbackVisitorBadgeTemplate(),
  subcontractorBadgeTemplateMatchesGeneralContractor: false,
  consentFormFields: [],
  consentFormLegals: [
    {
      languageId: '01a154c7-dc37-450a-b0d1-1bed17f4a3be',
      languageCode: 'en' as any,
      name: '',
      body: '',
      htmlBody: null,
    },
    {
      languageId: '02ac0942-db28-44a1-8cd4-9cd16e1579b7',
      languageCode: 'es' as any,
      name: '',
      body: '',
      htmlBody: null,
    },
  ],
  setupNotes: '',
  badgeTemplates: [],
  schedules: [],
  projectCodes: [{ code: '', canShowToGC: false, canShowToSub: false, projectCodeVisibilityRules: [] }],
});

export const billingModelTypeMap = {
  [BillingModelType.BADGES]: 'badgeBillingModel',
  [BillingModelType.SEATS]: 'seatBillingModel',
};

export enum DetailTabType {
  CLIENTS = 'clients',
  COMPANIES = 'companies',
  USERS = 'users',
  WORKERS = 'workers',
  VISITORS = 'visitors',
  ACS = 'acs',
  BPS = 'bps',
  INVOICES = 'invoices',
  COMMUNICATIONS = 'communications',
  INFORMATION = 'information',
}

export const tabMap: {
  [key: string]: { id: string; key: string; title: string; roleList?: Role[]; permissionsExpression?: string };
} = {
  [DetailTabType.CLIENTS]: {
    id: 'clients',
    key: 'clients',
    title: 'Companies',
    permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
  },
  [DetailTabType.COMPANIES]: {
    id: 'companies',
    key: 'companies',
    title: 'Companies',
    permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
  },
  [DetailTabType.USERS]: {
    id: 'users',
    key: 'users',
    title: 'Users',
    permissionsExpression: `${UserModel.UsersPermission.VIEWACCESS}`,
  },
  [DetailTabType.WORKERS]: {
    id: 'workers',
    key: 'workers',
    title: 'Workers',
    permissionsExpression: `${UserModel.WorkersPermission.VIEWACCESS}`,
  },
  [DetailTabType.VISITORS]: {
    id: 'visitors',
    key: 'visitors',
    title: 'Visitors',
    permissionsExpression: `${UserModel.VisitorsPermission.VIEWACCESS}`,
  },
  [DetailTabType.ACS]: {
    id: 'acs',
    key: 'acs',
    title: 'ACS',
    permissionsExpression: UserModel.AccessControlSystemsPermission.VIEWACCESS,
  },
  [DetailTabType.BPS]: {
    id: 'bps',
    key: 'bps',
    title: 'BPS',
    permissionsExpression: UserModel.BadgePrintingSystemsPermission.VIEWACCESS,
  },
  [DetailTabType.INVOICES]: {
    id: 'invoices',
    key: 'invoices',
    title: 'Invoices',
    permissionsExpression: `${UserModel.InvoicesPermission.VIEWACCESS}`,
  },
  [DetailTabType.COMMUNICATIONS]: {
    id: 'communications',
    key: 'communications',
    title: 'Communications',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  [DetailTabType.INFORMATION]: {
    id: 'information',
    key: 'information',
    title: 'Information',
    permissionsExpression: UserModel.ProjectsPermission.VIEWACCESS,
  },
};

export interface IAssignClientProject {
  id: string;
  role: CompanyRole;
  isTaxExempt?: boolean;
  invitationSentToCompanyUsers?: Array<string>;
  selectedExternallyAddedUsers?: Array<string>;
}

export interface IClientTaxCondition {
  companyId: string;
  isTaxExempt: boolean;
}

export interface IWorkerProject {
  id?: string;
  project: INamedEntity;
  jobSiteAddress?: IAddress;
  badgeStatus?: BadgeStatus;
  badgeType?: BadgeType;
  consentForm: IConsentForm;
  category: INamedEntity;
  company: INamedEntity;
  endDate: string;
  startDate: string;
  trades: INamedEntity[];
  workerProjectStatus: WorkerProjectStatus;
  badgeId?: string;
  scannedFileUrl: string;
  sourceType: SourceType;
  timeZone?: ITimeZone;
  projectId?: string;
}

export const roleMap = {
  [CompanyRole.DEVELOPER]: 'Developer',
  [CompanyRole.GENERAL_CONTRACTOR]: 'General Contractor',
  [CompanyRole.UNVERIFIED_CLIENTS]: 'Unverified Companies',
  [CompanyRole.SUB_CONTRACTOR]: 'Subcontractor',
  [CompanyRole.DEVELOPER_PROJECT_OWNER]: 'Project Owner',
};

export const roleListOptions = [
  { value: '', label: 'All Roles' },
  { value: CompanyRole.DEVELOPER, label: roleMap[CompanyRole.DEVELOPER] },
  { value: CompanyRole.GENERAL_CONTRACTOR, label: roleMap[CompanyRole.GENERAL_CONTRACTOR] },
  { value: CompanyRole.SUB_CONTRACTOR, label: roleMap[CompanyRole.SUB_CONTRACTOR] },
  { value: CompanyRole.DEVELOPER_PROJECT_OWNER, label: roleMap[CompanyRole.DEVELOPER_PROJECT_OWNER] },
];

export const widgetMap = {
  Draft: {
    status: 'Draft',
    content: 'Review',
    total: (value: number) => value,
  },
  Pending: {
    status: 'Pending Approval',
    content: 'Review',
    total: (value: number) => value,
  },
  Active: {
    status: 'Active',
    content: 'Review',
    total: (value: number) => value,
  },
  Invitation: {
    status: 'Pending Invitation',
    content: '',
    total: (value: number) => value,
  },
  Invoices: {
    status: 'Invoices',
    content: '',
    total: (value: number) => `$ ${formatNumberWithCommas(value)}`,
  },
};

export const getFallbackRelatedCompany = (tempId: string = null, role = CompanyRole.DEVELOPER): IProjectCompany => ({
  id: null,
  role,
  name: '',
  status: null,
  tempId,
  isTaxExempt: false,
  ...(role === CompanyRole.GENERAL_CONTRACTOR ? { canViewAllWorkers: true, canAssignAllWorkers: true, canCreateOrManageAllWorkers: true } : {}),
});

export enum BadgeOptions {
  TEMPLATE = 0,
  CUSTOM = 1,
}

export const workerProjectStatus = {
  [WorkerProjectStatus.PENDING]: 'Pending',
  [WorkerProjectStatus.ACCEPTED]: 'Accepted',
  [WorkerProjectStatus.DECLINED]: 'Declined',
};

export const workerProjectStatusList = [
  { value: '', label: 'All Status in Project' },
  { value: WorkerProjectStatus.PENDING, label: workerProjectStatus[WorkerProjectStatus.PENDING] },
  { value: WorkerProjectStatus.ACCEPTED, label: workerProjectStatus[WorkerProjectStatus.ACCEPTED] },
  // { value: WorkerProjectStatus.DECLINED, label: workerProjectStatus[WorkerProjectStatus.DECLINED] },
];

export const roleAcronymMap = {
  [CompanyRole.DEVELOPER]: 'DEV',
  [CompanyRole.GENERAL_CONTRACTOR]: 'GC',
  [CompanyRole.SUB_CONTRACTOR]: 'SUB',
  [CompanyRole.DEVELOPER_PROJECT_OWNER]: 'PO',
};

export enum PredefinedTrigger {
  PROJECT_ACTIVATION = 0,
  NEW_SUB_ENROLLMENT = 1,
  FAILED_PAYMENT = 2,
  ACS = 3,
  ACS_HANDLED_ITEM_ASSIGNED = 4,
  ACS_TURNSTILE_ITEM_ASSIGNED = 5,
  ACS_OPEN_PORTAL_ITEM_ASSIGNED = 6,
  PROJECT_ACTIVATION_AND_ACS = 7,
  BPS = 8,
  BPS_ITEM_ASSIGNED = 9,
  PROJECT_ACTIVATION_AND_BPS = 10,
}

export const predefinedTriggerIds = {
  [PredefinedTrigger.PROJECT_ACTIVATION]: '2a89c3a6-1081-432a-8a7b-19e2d6cb8613',
  [PredefinedTrigger.NEW_SUB_ENROLLMENT]: 'f77d3f67-0356-4f0e-8999-172f9e555e98',
  [PredefinedTrigger.FAILED_PAYMENT]: '6ff1cafd-a5d2-42e0-bbc5-e754669383f2',
  [PredefinedTrigger.ACS]: '1da02837-e3c4-425a-b00d-d79d7cbbab26',
  [PredefinedTrigger.ACS_HANDLED_ITEM_ASSIGNED]: '906ea184-8542-44de-a967-3dbcaa4f18c1',
  [PredefinedTrigger.ACS_TURNSTILE_ITEM_ASSIGNED]: '6e4dd8e9-ec7d-4352-8174-d8414971df67',
  [PredefinedTrigger.ACS_OPEN_PORTAL_ITEM_ASSIGNED]: '661e5dca-66eb-4753-ad55-a6a045286ba1',
  [PredefinedTrigger.PROJECT_ACTIVATION_AND_ACS]: '41e84485-5180-4d7f-9612-6ff98728d832',
  [PredefinedTrigger.BPS]: '5ec8a358-6e0d-4e70-831e-59cdaa34225f',
  [PredefinedTrigger.BPS_ITEM_ASSIGNED]: '3914a75b-3183-4310-a73c-0ad8b35d383e',
  [PredefinedTrigger.PROJECT_ACTIVATION_AND_BPS]: '8ec4b5f6-0b36-4ba3-ad0e-feb91ea3b6c7',
};

export enum BillingEngineRuleCompanyType {
  All = 1,
  AllProjectOwners = 2,
  AllDevelopers = 3,
  AllGeneralContractors = 4,
  AllSubcontractors = 5,
  SelectedCompany = 6,
}

export const billingEngineRuleCompanyTypeMap = {
  [BillingEngineRuleCompanyType.AllProjectOwners]: 'All Project Owners',
  [BillingEngineRuleCompanyType.AllDevelopers]: 'All Developers',
  [BillingEngineRuleCompanyType.AllGeneralContractors]: 'All General Contractors',
  [BillingEngineRuleCompanyType.AllSubcontractors]: 'All Subcontractors',
};

export enum BillingEngineRuleItemType {
  Product = 1,
  ProductBundle = 2,
}

export enum BillingEngineRuleStatus {
  Active = 1,
  Inactive = 2,
  Retired = 3,
}

export const validateBillingEngineCompanyList = ({ value }) => {
  if (value && value.length > 0) {
    let result: any = {};
    value.forEach((company, index) => {
      if (!company?.responsibleCompanyId) {
        if (!result[index]) result[index] = {};
        result[index].responsibleCompanyId = 'is required';
      }
      if (!company?.coveredCompanyId?.length) {
        if (!result[index]) result[index] = {};
        result[index].coveredCompanyId = 'is required';
      }
    });
    if (Object.keys(result).length) {
      return result;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const validateBillingEngineProductList = ({ value }) => {
  if (value && value.length > 0) {
    let result: any = {};
    value.forEach((product, index) => {
      if (!product?.subTotal) {
        if (!result[index]) result[index] = {};
        result[index].subTotal = 'is required';
      } else if (product?.subTotal < 0 || product?.subTotal > 999999.99) {
        if (!result[index]) result[index] = {};
        result[index].subTotal = 'Subtotal cannot be less than 0 or more than 999999.99';
      } else if (product?.subTotal > product?.unitPrice * product?.units) {
        if (!result[index]) result[index] = {};
        result[index].subTotal = 'Subtotal cannot be more than (Units * Units Price)';
      }
      if (!product?.unitPrice) {
        if (!result[index]) result[index] = {};
        result[index].unitPrice = 'is required';
      } else if (product?.unitPrice < 0 || product?.unitPrice > 999999.99) {
        if (!result[index]) result[index] = {};
        result[index].unitPrice = 'Unit Price cannot be less than 0 or more than 999999.99';
      }
      if (!product?.units) {
        if (!result[index]) result[index] = {};
        result[index].units = 'is required';
      } else if (product?.units < 0) {
        if (!result[index]) result[index] = {};
        result[index].units = 'Units cannot be less than 0';
      }
      if (product?.maxNoOfTransactions === 0) {
        if (!result[index]) result[index] = {};
        result[index].maxNoOfTransactions = 'Max transactions must be greater than 0';
      }
      if (!product.responsibleCompanyId) {
        if (!result[index]) result[index] = {};
        result[index].responsibleCompanyId = 'is required';
      }
      if (!product.invoiceSchedule) {
        if (!result[index]) result[index] = {};
        result[index].invoiceSchedule = 'is required';
      }
      if (product.isClientResponsibleForThemselves && product.isClientCoverOtherClients) {
        if (product?.companies?.length) {
          product.companies.forEach((company, i) => {
            if (!company?.responsibleCompanyId) {
              if (!result[index]) result[index] = {};
              if (!result[index]?.companies) result[index].companies = {};
              if (!result[index].companies[i]) result[index].companies[i] = {};
              result[index].companies[i].responsibleCompanyId = 'is required';
            }
            if (!company?.coveredCompanyId || !company?.coveredCompanyId?.filter(item => item.id !== '-1')?.length) {
              if (!result[index]) result[index] = {};
              if (!result[index]?.companies) result[index].companies = {};
              if (!result[index].companies[i]) result[index].companies[i] = {};
              result[index].companies[i].coveredCompanyId = 'is required';
            }
          });
        }
      }
    });
    if (Object.keys(result).length) {
      return result;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const billingEngineformRules = (): IFormRules => ({
  categories: {
    required: true,
    rules: [],
  },
  products: {
    required: true,
    rules: [validateBillingEngineProductList],
  },
});

export const billingProductBundleformRules = (): IFormRules => ({
  categories: {
    required: true,
    rules: [],
  },
  items: {
    required: true,
    rules: [],
  },
  name: {
    required: true,
    rules: [],
  },
  pricePerUnit: {
    required: true,
    rules: [],
  },
  billingTriggerId: {
    required: true,
    rules: [],
  },
  billingFrequency: {
    required: true,
    rules: [],
  },
  customBillingTrigger: {
    required: false,
    rules: [],
  },
  recurrentRegularBillingFrequency: {
    required: false,
    rules: [],
  },
  billingFrequencyPeriodForEvery: {
    required: false,
    rules: [],
  },
  every: {
    required: false,
    rules: [],
  },
  quickBooksAccountId: {
    required: true,
    rules: [],
  },
});

export const getBillingEngineRulesFallback = () => ({
  categories: [],
  products: [],
  includePricingAgreement: false,
  companies: [
    {
      responsibleCompanyId: '',
      coveredCompanyType: 0,
      coveredCompanyId: [],
    },
  ],
  isClientResponsibleForThemselves: false,
  isClientCoverOtherClients: false,
});

export const getBillingEngineProductBundlesFallback = () => ({
  categories: [],
  items: [],
  name: '',
  every: null,
  pricePerUnit: null,
  billingFrequency: null,
  billingTriggerId: null,
  customBillingTrigger: null,
  billingFrequencyPeriodForEvery: null,
  recurrentRegularBillingFrequency: null,
});

export enum BillingScope {
  None = 0,
  Project = 1,
  Client = 2,
}

export const companyTypeRoleMap = {
  [BillingEngineRuleCompanyType.AllDevelopers]: CompanyRole.DEVELOPER,
  [BillingEngineRuleCompanyType.AllGeneralContractors]: CompanyRole.GENERAL_CONTRACTOR,
  [BillingEngineRuleCompanyType.AllProjectOwners]: CompanyRole.DEVELOPER_PROJECT_OWNER,
  [BillingEngineRuleCompanyType.AllSubcontractors]: CompanyRole.SUB_CONTRACTOR,
};

export enum ProjectManagedByType
{
  GCManaged = 1,
  FCAManaged = 2
}

export const projectManagedByTypeIdMap = {
  [ProjectManagedByType.GCManaged]: "8dca028c-c1b4-4218-ae66-2811ffe8ed8a",
  [ProjectManagedByType.FCAManaged]: "d24fe378-040b-440d-bc6b-51ac47e5ed79",
};

export const projectManagedByTypeNameMap = {
  [ProjectManagedByType.GCManaged]: "GC-managed",
  [ProjectManagedByType.FCAManaged]: "FCA-managed",
};
